import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { mockPurchaseRequestService } from '@/services/mock/MockPurchaseRequestService';
import { PurchaseRequest } from '@/types/domain';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ROUTES } from '@/constants/routes';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import {
  Search,
  RefreshCw,
  Eye,
  Pencil,
  Trash2,
  X,
  Layers,
  RotateCcw,
  Loader2,
  Plus,
} from 'lucide-react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { RequestDetailsPage } from './RequestDetailsPage';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';

export const RequestsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { can, isAuditorReadOnly } = usePermission();

  // Filter States - Sync tab with URL so Drafts can be linked directly or viewed in isolation
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(tabFromUrl || 'ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [selectedRequestForDrawer, setSelectedRequestForDrawer] = useState<string | null>(null);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    if (newTab === 'ALL') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', newTab);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Delete Confirmation Modal State
  const [requestToDelete, setRequestToDelete] = useState<PurchaseRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fast 100ms debounce so typing REQ numbers triggers instantaneously without stutter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 100);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Lock body scroll and handle Escape key for modal
  useEffect(() => {
    if (requestToDelete) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isDeleting) setRequestToDelete(null);
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [requestToDelete, isDeleting]);

  const isFiltered = activeTab !== 'ALL' || categoryFilter !== 'ALL' || searchQuery.trim() !== '';

  const handleClearFilters = () => {
    handleTabChange('ALL');
    setCategoryFilter('ALL');
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Infinite Scroll Hook configured for 10 records initial load & smooth scroll loading
  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = await mockPurchaseRequestService.getRequests({
        status: activeTab,
        category: categoryFilter,
        search: debouncedSearch,
        page: params.page || 1,
        pageSize: params.pageSize || 10,
        scope: user?.accessScope
          ? {
              ownership: user.accessScope.ownership,
              userId: user.id,
              userName: user.name,
              departments: user.accessScope.departments,
              plants: user.accessScope.plants,
            }
          : undefined,
      });
      return {
        data: res.requests,
        page: params.page || 1,
        pageSize: params.pageSize || 10,
        total: res.total,
        totalPages: Math.ceil(res.total / (params.pageSize || 10)),
        hasNextPage: (params.page || 1) * (params.pageSize || 10) < res.total,
        hasPreviousPage: (params.page || 1) > 1,
      };
    },
    [activeTab, categoryFilter, debouncedSearch, user]
  );

  const {
    items: requests,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total,
    sentinelRef,
    retry,
    removeLocalItem,
  } = useInfiniteScroll<PurchaseRequest>({
    fetchFn,
    pageSize: 10,
    params: {
      search: debouncedSearch,
      filters: {
        status: activeTab,
        category: categoryFilter,
      },
    },
  });

  const handleConfirmDelete = async () => {
    if (!requestToDelete) return;
    const targetId = requestToDelete.id;
    setIsDeleting(true);
    try {
      // 1. Immediately trigger the cutting / vanishing row animation
      removeLocalItem(targetId);
      // 2. Persist deletion in mock service
      await mockPurchaseRequestService.deleteRequest(targetId);
      // 3. Smoothly close modal
      setRequestToDelete(null);
    } catch (err) {
      console.error('Failed to delete requisition:', err);
      retry();
    } finally {
      setIsDeleting(false);
    }
  };

  // Status Filter Tabs (Segmented Pills - Squeezed)
  const TABS = [
    { label: 'All', value: 'ALL' },
    { label: 'Drafts', value: 'Draft' },
    { label: 'Pending', value: 'PendingApproval' },
    { label: 'Under Review', value: 'UnderReview' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Rejected', value: 'Rejected' },
  ];

  // Category Filter Segmented Pills (Squeezed to prevent wrapping)
  const CATEGORIES = [
    { label: 'All', value: 'ALL' },
    { label: 'IT Hardware', value: 'IT Hardware' },
    { label: 'OpEx', value: 'OperatingExpense' },
    { label: 'CapEx', value: 'CapitalEquipment' },
    { label: 'Raw Material', value: 'RawMaterial' },
  ];

  const columns: ColumnDef<PurchaseRequest>[] = [
    {
      header: 'REQ Number',
      accessorKey: 'requestNumber',
      cell: (row) => (
        <span className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap">
          {row.requestNumber}
        </span>
      ),
    },
    {
      header: 'Title & Justification',
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[190px] xl:max-w-[220px]">
          <span className="font-bold text-foreground truncate" title={row.title}>{row.title}</span>
          <span className="text-xs text-foreground-secondary truncate" title={row.justification}>{row.justification}</span>
        </div>
      ),
    },
    {
      header: 'Department',
      accessorKey: 'departmentName',
      cell: (row) => (
        <div className="max-w-[130px] xl:max-w-[160px] truncate" title={row.departmentName}>
          <span className="text-xs font-semibold text-foreground-secondary truncate">{row.departmentName}</span>
        </div>
      ),
    },
    {
      header: 'Category',
      cell: (row) => (
        <Badge variant="outline" className="text-[10px] font-bold whitespace-nowrap">
          {row.category}
        </Badge>
      ),
    },
    {
      header: 'Estimated Value',
      cell: (row) => (
        <span className="font-poppins font-bold text-foreground font-mono whitespace-nowrap">
          ₹{row.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center">
          <StatusBadge
            status={row.status}
            category="PurchaseRequest"
            className="w-32 py-1 justify-center text-center font-semibold text-xs rounded-full shadow-2xs font-poppins whitespace-nowrap"
          />
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedRequestForDrawer(row.id)}
            title="View Details (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>
          {can('REQUEST_EDIT') && !isAuditorReadOnly() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.REQUESTS.EDIT(row.id))}
              title="Edit Requisition"
              className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {can('REQUEST_DELETE') && !isAuditorReadOnly() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRequestToDelete(row)}
              className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
              title="Delete Requisition"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 font-poppins">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground font-poppins">
            Purchase Requisitions Portal
          </h1>
          <p className="text-sm font-semibold text-foreground-secondary">
            Enterprise procurement requests ledger • Showing {requests.length} of {total} records
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can('REQUEST_CREATE') && !isAuditorReadOnly() && (
            <Button
              size="sm"
              onClick={() => navigate(ROUTES.REQUESTS.NEW)}
              className="cursor-pointer font-poppins font-semibold shadow-xs"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Create Requisition
            </Button>
          )}
        </div>
      </div>

      {/* Segmented Pill Filters & Unified Search Controls */}
      <Card className="rounded-2xl border border-border shadow-xs overflow-x-hidden">
        <CardHeader className="pb-4 border-b border-border space-y-3">
          {/* Top Row: Search Input & Refresh Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search requisition #, title, requester, or department..."
                className="w-full pl-9 pr-8 py-2 bg-surface border border-border rounded-xl text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all font-medium"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground cursor-pointer"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <span className="text-xs text-foreground-muted font-bold">
                {total} Total Requisitions
              </span>
              <Button variant="ghost" size="sm" onClick={retry} title="Refresh Table" className="cursor-pointer">
                <RefreshCw className="h-3.5 w-3.5 text-foreground-secondary" />
              </Button>
            </div>
          </div>

          {/* Bottom Row: Squeezed Status Filter Segmented Pills, Category Pills & Inline Clear Filters - Both Styled with Harmonized Poppins Typography */}
          <div className="flex flex-wrap items-center gap-2 pt-1 font-poppins">
            {/* Status Pills with Sliding Framer Motion Highlight */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border shrink-0 font-poppins">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => handleTabChange(tab.value)}
                    className={`relative px-3 py-1.5 rounded-lg text-xs font-poppins font-medium transition-colors whitespace-nowrap cursor-pointer z-10 ${
                      isActive
                        ? 'text-primary-foreground font-semibold'
                        : 'text-foreground-secondary hover:text-foreground'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeStatusPill"
                        className="absolute inset-0 rounded-lg bg-primary shadow-xs -z-10"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Category Segmented Pills with Sliding Framer Motion Highlight - Identical Poppins Font and Size */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border shrink-0 font-poppins">
              <span className="text-xs font-semibold text-foreground flex items-center gap-1.5 px-2 font-poppins">
                <Layers className="h-3.5 w-3.5 text-primary" /> Category:
              </span>
              {CATEGORIES.map((cat) => {
                const isActive = categoryFilter === cat.value;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategoryFilter(cat.value)}
                    className={`relative px-3 py-1.5 rounded-lg text-xs font-poppins font-medium transition-colors whitespace-nowrap cursor-pointer z-10 ${
                      isActive
                        ? 'text-primary-foreground font-semibold'
                        : 'text-foreground-secondary hover:text-foreground'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeCategoryPill"
                        className="absolute inset-0 rounded-lg bg-primary shadow-xs -z-10"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Clear Filters Option Pill with Framer Motion Transition - Always Inline */}
            <AnimatePresence>
              {isFiltered && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8, x: -6 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: -6 }}
                  transition={{ duration: 0.15 }}
                  type="button"
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 rounded-lg text-xs font-poppins font-bold text-rose-600 dark:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/25 flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap shadow-2xs shrink-0"
                  title="Reset all filters and search"
                >
                  <RotateCcw className="h-3.5 w-3.5 stroke-[2.5]" />
                  <span>Clear Filters</span>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </CardHeader>

        {/* Fixed Non-Moving Table with Sticky Header, AnimatePresence Vanishing & Smooth 10-item Lazy Loading */}
        <CardContent className="p-4 overflow-x-hidden">
          <DataTable
            data={requests}
            columns={columns}
            isLoading={isLoading}
            pageSize={10}
            totalCount={total}
            infiniteScroll={true}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            sentinelRef={sentinelRef}
            hideSearch={true}
            fixedHeight={true}
            containerHeightClass="max-h-[560px] overflow-y-auto overflow-x-hidden"
            emptyTitle="Data Not Found"
            emptyDescription="No requisitions match your current filter or search criteria."
            onClearFilters={handleClearFilters}
            onRowClick={(row) => navigate(ROUTES.REQUESTS.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Popup Modal with Framer Motion Spring Animations & Blurred Backdrop */}
      <AnimatePresence>
        {requestToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
            {/* Blurred Backdrop with Fade Animation */}
            <motion.div
              key="delete-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
              onClick={() => !isDeleting && setRequestToDelete(null)}
            />

            {/* Modal Dialog Card with Premium Smooth Spring Animation */}
            <motion.div
              key="delete-dialog-card"
              role="dialog"
              aria-modal="true"
              initial={{ opacity: 0, scale: 0.88, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 16 }}
              transition={{ type: 'spring', damping: 26, stiffness: 360, mass: 0.8 }}
              className="relative z-10 w-full max-w-md rounded-3xl border border-border/80 bg-white dark:bg-[#121526] text-foreground p-6 shadow-2xl space-y-5"
            >
              {/* Header: Icon & Close Button */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3.5">
                  <div className="h-12 w-12 rounded-2xl bg-rose-500/15 border border-rose-500/25 flex items-center justify-center text-rose-600 dark:text-rose-400">
                    <Trash2 className="h-6 w-6 stroke-[2.2]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black tracking-tight text-foreground">
                      Delete Requisition?
                    </h3>
                    <p className="text-xs font-semibold text-foreground-secondary">
                      Confirm removal of draft purchase request
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setRequestToDelete(null)}
                  className="rounded-xl p-1.5 text-foreground-secondary hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Requisition Details Preview Box */}
              <div className="rounded-2xl border border-border/70 bg-muted/40 p-4 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Requisition Number
                  </span>
                  <span className="font-mono font-extrabold text-primary">
                    {requestToDelete.requestNumber}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px] whitespace-nowrap">
                    Title
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[220px] text-right">
                    {requestToDelete.title}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Estimated Value
                  </span>
                  <span className="font-extrabold text-foreground font-mono">
                    ₹{requestToDelete.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* Warning Message */}
              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to delete this requisition? This action cannot be undone and will permanently remove this draft from the system.
              </p>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setRequestToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Delete Requisition</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Right Slide-Over Curtain Drawer for Requisition Details */}
      <SlideOverDrawer
        isOpen={Boolean(selectedRequestForDrawer)}
        onClose={() => setSelectedRequestForDrawer(null)}
        title="Purchase Requisition Dossier"
        subtitle="Internal requisition specifications, financial approvals, budget impact, and audit history"
        fullPageUrl={selectedRequestForDrawer ? ROUTES.REQUESTS.DETAILS(selectedRequestForDrawer) : undefined}
      >
        {selectedRequestForDrawer && (
          <RequestDetailsPage
            recordId={selectedRequestForDrawer}
            isDrawer={true}
            onClose={() => setSelectedRequestForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
